import { useEffect, useMemo, useState } from 'react'
import { useAppDispatch } from '../AppContext'
import { Actions } from '../AppReducer'
import SearchTrackControls from './SearchTrackControls'
import { useQuery } from '@apollo/client/react'
import SearchTracks from '../graphql/queries/SearchTracks'
import useCurrentUser from '../hooks/useCurrentUser'
import useCurrentRoom from '../hooks/useCurrentRoom'
import { Box, Select, TextInput, Group, Stack, Text, Table, Loader, Center } from '@mantine/core'
import '@mantine/core/styles.css'
import Joyride, { STATUS } from 'react-joyride'
import TagStringEditor from '../ui/TagStringEditor'

export default function Search() {

    const dispatch = useAppDispatch()
    const user = useCurrentUser()
    const currentRoom = useCurrentRoom()

    const isAdmin = user?.roles?.includes('admin')

    const collections = [{ name: 'Tunistry Tracks', value: 'TRACK' }, { name: 'My Uploads', value: 'USER' }]

    const blankParams = useMemo(() => {
        const lastSearchData = localStorage.getItem('lastSearch')
        const lastSearchParams = lastSearchData ? JSON.parse(lastSearchData) : {}
        return {
            searchType: lastSearchParams.searchType ?? 'ARTIST',
                text: lastSearchParams.text ?? '',
            tags: lastSearchParams.tags ?? '',
            // Override collection to TRACK if user is not an admin
            searchCollection: isAdmin ? (lastSearchParams.searchCollection ?? 'TRACK') : 'TRACK',
            roomId: currentRoom.id
        }
    }, [isAdmin, currentRoom.id])
    const [params, setParams] = useState(blankParams)

    useEffect(() => { setParams(blankParams) }, [user?.id, blankParams])

    const {
        loading: tracksLoading,
        data: trackData,
        fetchMore
    } = useQuery(SearchTracks, {
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
        variables: { params: { ...params, tags: params.tags.toLowerCase().split(/\s+/).filter(Boolean) } }
    })
    const trackList = trackData?.searchTracks?.tracks

    function handleScroll(e) {
        const {scrollTop, scrollHeight, clientHeight} = e.target
        const position = Math.ceil(
            (scrollTop / (scrollHeight - clientHeight)) * 100
        )
        if (!tracksLoading && position > 80 && trackData?.searchTracks?.cursor) {
            console.log(`fetching more tracks`)
            fetchMore({ variables: { cursor: trackData.searchTracks.cursor }})
        }
    }

    function collectionUpdated(value) {
        console.log(`collectionUpdated => ${value}`)
        let newParams
        if (value === 'TRACK') {
            newParams = { ...params, searchCollection: 'TRACK', roomId: currentRoom.id }
        } else { // USER
            newParams = { ...params, searchCollection: 'USER', roomId: currentRoom.id }
        }
        setParams(newParams)
        localStorage.setItem('lastSearch', JSON.stringify(newParams))
    }

    function paramsUpdated(field, value) {
        console.log(`paramsUpdated => ${field}: ${value}`)
        const newParams = { ...params }
        newParams[field] = value
        setParams(newParams)
        localStorage.setItem('lastSearch', JSON.stringify(newParams))
    }

    let noResultsContent
    if (!tracksLoading)
        noResultsContent =
            <Box p="xl" ta="center">
                <Text c="dimmed" size="lg" mb="md">No matches for this search.</Text>
                <Text c="dimmed" size="sm">
                    To add music you can{' '}
                    <Text
                        component="span"
                        c="blue"
                        style={{ cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={() => dispatch(Actions.navigateTo('upload'))}
                    >
                        upload your music files
                    </Text>
                    {' '}or purchase new digital music from one of our affiliates.
                </Text>
            </Box>

    return (
        <Box style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Search Parameters */}
            <Box
                p="sm"
                style={{
                    borderBottom: '1px solid rgba(255,255,255,0.08)',
                    backgroundColor: 'rgba(0,0,0,0.2)'
                }}
            >
                <Stack gap="xs">
                    {isAdmin && (
                        <Group gap="xs" wrap="nowrap">
                            <span className="material-symbols-outlined" style={{ color: 'var(--mantine-color-dimmed)' }}>folder</span>
                            <Select
                                value={params.searchCollection}
                                onChange={(value) => collectionUpdated(value)}
                                data={collections.map(c => ({ value: c.value, label: c.name }))}
                                placeholder="Select collection"
                                style={{ flex: 1 }}
                                withinPortal={false}
                                comboboxProps={{ zIndex: 2000 }}
                            />
                        </Group>
                    )}
                    <Group gap="xs" wrap="nowrap">
                        <span className="material-symbols-outlined" style={{ color: 'var(--mantine-color-dimmed)' }}>search</span>
                        <Select
                            value={params.searchType}
                            onChange={(value) => paramsUpdated('searchType', value)}
                            data={[
                                { value: 'ARTIST', label: 'Artist' },
                                { value: 'TITLE', label: 'Title' },
                                { value: 'ALBUM', label: 'Album' }
                            ]}
                            placeholder="Search by..."
                            style={{ flex: '0 0 120px' }}
                            withinPortal={false}
                            comboboxProps={{ zIndex: 2000 }}
                            data-tour="search-type"
                        />
                        <TextInput
                            placeholder="Enter search text..."
                            value={params.text}
                            onChange={(e) => paramsUpdated('text', e.currentTarget.value)}
                            style={{ flex: 1 }}
                        />
                    </Group>
                    <Group gap="xs" wrap="nowrap">
                        <span className="material-symbols-outlined" style={{ color: 'var(--mantine-color-dimmed)' }}>tag</span>
                        <TagStringEditor
                            roomId={params.roomId}
                            placeholder="Enter tags separated by spaces..."
                            value={params.tags}
                            onChange={(value) => paramsUpdated('tags', value)}
                            style={{ flex: 1 }}
                            data-tour="search-tags"
                        />
                    </Group>
                </Stack>
            </Box>

            {/* Search Results */}
            <Box
                style={{
                    flex: 1,
                    overflowY: 'auto',
                    position: 'relative'
                }}
                onScroll={handleScroll}
            >
                {trackList?.length > 0 ? (
                    <Table highlightOnHover>
                        <Table.Thead style={{ position: 'sticky', top: 0, backgroundColor: 'var(--mantine-color-dark-7)', zIndex: 1 }}>
                            <Table.Tr>
                                <Table.Th style={{ width: '120px', minWidth: '120px', textAlign: 'center' }}></Table.Th>
                                <Table.Th style={{ textAlign: 'center' }}>Artist</Table.Th>
                                <Table.Th style={{ textAlign: 'center' }}>Title</Table.Th>
                                <Table.Th style={{ textAlign: 'center' }}>Album</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {trackList.map((track, index) => (
                                <Table.Tr key={`${track.id}-${index}`}>
                                    <Table.Td style={{ width: '120px', minWidth: '120px', textAlign: 'center' }}>
                                        <SearchTrackControls track={track} isFirstRow={index === 0} />
                                    </Table.Td>
                                    <Table.Td style={{ textAlign: 'center' }}>
                                        <Text size="sm">{track.artist}</Text>
                                    </Table.Td>
                                    <Table.Td style={{ textAlign: 'center' }}>
                                        <Text
                                            size="sm"
                                            c="blue"
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => dispatch(Actions.showTrackModal(track.id))}
                                        >
                                            {track.title ?? '[ untitled ]'}
                                        </Text>
                                    </Table.Td>
                                    <Table.Td style={{ textAlign: 'center' }}>
                                        <Text
                                            size="sm"
                                            c="blue"
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => dispatch(Actions.showAlbumModal(track))}
                                        >
                                            {track.album}
                                        </Text>
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                ) : (
                    noResultsContent
                )}
                {tracksLoading && (
                    <Center p="xl">
                        <Loader size="md" />
                    </Center>
                )}
            </Box>
        </Box>
    )
}
